import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubmissions } from '../hooks/useSubmissions';
import { useTools } from '../hooks/useTools';
import { supabase } from '../lib/supabase';
import { GlassCard } from '../components/ui/GlassCard';
import { Check, X, ExternalLink, RefreshCw, LogOut, Plus, Trash2, Star, Edit, Save, XCircle, Image as ImageIcon, Video, Music, Code, PenTool, Palette, Zap, Search, Sparkles, Globe, Shield } from 'lucide-react';
import { AdminAddToolModal } from '../components/AdminAddToolModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Tool, PricingTier } from '../types/database';
const CATEGORIES = [{
  name: 'Image',
  icon: ImageIcon
}, {
  name: 'Video',
  icon: Video
}, {
  name: 'Audio',
  icon: Music
}, {
  name: 'Development',
  icon: Code
}, {
  name: 'Writing',
  icon: PenTool
}, {
  name: 'Design',
  icon: Palette
}, {
  name: 'Productivity',
  icon: Zap
}, {
  name: 'Research',
  icon: Search
}, {
  name: 'Other',
  icon: Sparkles
}];
export function AdminDashboardPage() {
  const navigate = useNavigate();
  const {
    submissions,
    loading: submissionsLoading,
    approveSubmission,
    rejectSubmission,
    refresh: refreshSubmissions
  } = useSubmissions();
  const {
    tools,
    loading: toolsLoading,
    refresh: refreshTools
  } = useTools();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'submissions' | 'tools'>('submissions');
  const [editingToolId, setEditingToolId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>(null);
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  useEffect(() => {
    checkAuth();
  }, []);
  const checkAuth = async () => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin');
    } else {
      setUserEmail(session.user.email || '');
    }
  };
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };
  const handleApprove = async (submission: any) => {
    setProcessingId(submission.id);
    await approveSubmission(submission);
    setProcessingId(null);
    refreshTools();
  };
  const handleReject = async (id: string) => {
    setProcessingId(id);
    await rejectSubmission(id);
    setProcessingId(null);
  };
  const startEditing = (tool: Tool) => {
    setEditingToolId(tool.id);
    setEditForm({
      name: tool.name,
      description: tool.description,
      website_url: tool.website_url,
      category: tool.category,
      logo_url: tool.logo_url,
      pricing_tiers: tool.pricing_tiers || []
    });
  };
  const cancelEditing = () => {
    setEditingToolId(null);
    setEditForm(null);
  };
  const saveEdit = async (toolId: string) => {
    try {
      const {
        error
      } = await supabase.from('tools').update({
        name: editForm.name,
        description: editForm.description,
        logo_url: editForm.logo_url,
        website_url: editForm.website_url,
        category: editForm.category,
        pricing_tiers: editForm.pricing_tiers
      }).eq('id', toolId);
      if (error) {
        console.error('Supabase error:', error);
        alert(`Failed to update tool: ${error.message}`);
        return;
      }
      setEditingToolId(null);
      setEditForm(null);
      refreshTools();
    } catch (err: any) {
      console.error('Error updating tool:', err);
      alert(`Failed to update tool: ${err.message}`);
    }
  };
  const handleDeleteTool = (tool: Tool) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Tool?',
      message: `Are you sure you want to delete "${tool.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const {
            error
          } = await supabase.from('tools').delete().eq('id', tool.id);
          if (error) {
            console.error('Supabase error:', error);
            alert(`Failed to delete tool: ${error.message}`);
            return;
          }
          refreshTools();
        } catch (err: any) {
          console.error('Error deleting tool:', err);
          alert(`Failed to delete tool: ${err.message}`);
        }
      }
    });
  };
  const handleToggleFeatured = (tool: Tool) => {
    const action = tool.is_featured ? 'unfeature' : 'feature';
    setConfirmDialog({
      isOpen: true,
      title: `${action === 'feature' ? 'Feature' : 'Unfeature'} Tool?`,
      message: `${action === 'feature' ? 'This tool will appear in "Most Popular Tools".' : 'This tool will be removed from "Most Popular Tools".'}`,
      onConfirm: async () => {
        try {
          const {
            error
          } = await supabase.from('tools').update({
            is_featured: !tool.is_featured
          }).eq('id', tool.id);
          if (error) {
            console.error('Supabase error:', error);
            alert(`Failed to update featured status: ${error.message}`);
            return;
          }
          refreshTools();
        } catch (err: any) {
          console.error('Error toggling featured status:', err);
          alert(`Failed to update featured status: ${err.message}`);
        }
      }
    });
  };
  const handleRefresh = () => {
    refreshSubmissions();
    refreshTools();
  };
  const updateEditForm = (field: string, value: any) => {
    setEditForm({
      ...editForm,
      [field]: value
    });
  };
  const addPricingTier = () => {
    setEditForm({
      ...editForm,
      pricing_tiers: [...editForm.pricing_tiers, {
        name: '',
        price: '',
        features: []
      }]
    });
  };
  const removePricingTier = (index: number) => {
    setEditForm({
      ...editForm,
      pricing_tiers: editForm.pricing_tiers.filter((_: any, i: number) => i !== index)
    });
  };
  const updatePricingTier = (index: number, field: string, value: any) => {
    const newTiers = [...editForm.pricing_tiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: value
    };
    setEditForm({
      ...editForm,
      pricing_tiers: newTiers
    });
  };
  const SelectedCategoryIcon = editForm ? CATEGORIES.find(c => c.name === editForm.category)?.icon || Sparkles : Sparkles;
  // Separate tools by source
  const adminTools = tools.filter(t => (t as any).source === 'admin');
  const scrapedTools = tools.filter(t => (t as any).source === 'scraped');
  return <div className="min-h-screen bg-black text-white p-8" data-id="element-657">
      <div className="max-w-7xl mx-auto" data-id="element-658">
        <div className="flex items-center justify-between mb-8" data-id="element-659">
          <div data-id="element-660">
            <h1 className="text-3xl font-bold font-special" data-id="element-661">Admin Dashboard</h1>
            {userEmail && <p className="text-sm text-gray-400 mt-1" data-id="element-662">
                Logged in as: {userEmail}
              </p>}
          </div>
          <div className="flex items-center gap-3" data-id="element-663">
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white font-medium transition-colors border border-white/10" data-id="element-664">
              <ExternalLink size={16} data-id="element-665" />
              View Site
            </a>
            <button onClick={() => setIsAddToolModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-colors" data-id="element-666">
              <Plus size={16} data-id="element-667" />
              Add Tool
            </button>
            <button onClick={handleRefresh} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" title="Refresh data" data-id="element-668">
              <RefreshCw size={20} data-id="element-669" />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors" data-id="element-670">
              <LogOut size={16} data-id="element-671" />
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10" data-id="element-672">
          <button onClick={() => setActiveTab('submissions')} className={`px-6 py-3 font-medium transition-all relative ${activeTab === 'submissions' ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`} data-id="element-673">
            Pending Submissions ({submissions.length})
            {activeTab === 'submissions' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" data-id="element-674" />}
          </button>
          <button onClick={() => setActiveTab('tools')} className={`px-6 py-3 font-medium transition-all relative ${activeTab === 'tools' ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`} data-id="element-675">
            All Tools ({tools.length})
            {activeTab === 'tools' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" data-id="element-676" />}
          </button>
        </div>

        {/* Pending Submissions Tab */}
        {activeTab === 'submissions' && <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-id="element-677">
            <div className="p-6 border-b border-white/10" data-id="element-678">
              <h2 className="text-xl font-bold" data-id="element-679">
                Pending Submissions ({submissions.length})
              </h2>
            </div>

            {submissionsLoading ? <div className="p-8 text-center text-gray-400" data-id="element-680">
                Loading submissions...
              </div> : submissions.length === 0 ? <div className="p-8 text-center text-gray-400" data-id="element-681">
                No pending submissions
              </div> : <div className="divide-y divide-white/10" data-id="element-682">
                {submissions.map(submission => <div key={submission.id} className="p-6 flex flex-col md:flex-row gap-6" data-id="element-683">
                    <div className="flex-1" data-id="element-684">
                      <div className="flex items-start gap-4 mb-4" data-id="element-685">
                        {submission.logo_url && <img src={submission.logo_url} alt={submission.name} className="w-12 h-12 rounded-lg object-cover bg-white/10" data-id="element-686" />}
                        <div data-id="element-687">
                          <h3 className="text-lg font-bold text-white" data-id="element-688">
                            {submission.name}
                          </h3>
                          <a href={submission.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm flex items-center gap-1 hover:underline" data-id="element-689">
                            {submission.website_url} <ExternalLink size={12} data-id="element-690" />
                          </a>
                        </div>
                      </div>

                      <p className="text-gray-300 mb-4" data-id="element-691">
                        {submission.description}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-4" data-id="element-692">
                        <span className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-693">
                          {submission.category}
                        </span>
                        {submission.pricing_tiers?.map((tier: any, i: number) => <span key={i} className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-694">
                              {tier.name}: {tier.price}
                            </span>)}
                      </div>

                      <div className="text-xs text-gray-500" data-id="element-695">
                        Submitted by: {submission.submitter_email}
                      </div>
                    </div>

                    <div className="flex flex-row md:flex-col gap-2 justify-center" data-id="element-696">
                      <button onClick={() => handleApprove(submission)} disabled={!!processingId} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors disabled:opacity-50" data-id="element-697">
                        <Check size={16} data-id="element-698" /> Approve
                      </button>
                      <button onClick={() => handleReject(submission.id)} disabled={!!processingId} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50" data-id="element-699">
                        <X size={16} data-id="element-700" /> Reject
                      </button>
                    </div>
                  </div>)}
              </div>}
          </div>}

        {/* All Tools Tab */}
        {activeTab === 'tools' && <div className="space-y-8" data-id="element-701">
            {toolsLoading ? <div className="p-8 text-center text-gray-400 bg-white/5 rounded-xl border border-white/10" data-id="element-702">
                Loading tools...
              </div> : tools.length === 0 ? <div className="p-8 text-center text-gray-400 bg-white/5 rounded-xl border border-white/10" data-id="element-703">
                No tools yet. Click "Add Tool" to get started!
              </div> : <>
                {/* Admin Tools Section */}
                {adminTools.length > 0 && <div data-id="element-704">
                    <div className="flex items-center gap-3 mb-4" data-id="element-705">
                      <Shield size={20} className="text-purple-400" data-id="element-706" />
                      <h2 className="text-xl font-bold text-white" data-id="element-707">
                        Curated Tools ({adminTools.length})
                      </h2>
                      <span className="text-sm text-gray-500" data-id="element-708">
                        — Uploaded via Admin
                      </span>
                    </div>
                    <div className="space-y-4" data-id="element-709">
                      {adminTools.map(tool => <div key={tool.id} className="bg-white/5 rounded-xl border border-white/10 p-6" data-id="element-710">
                          {editingToolId === tool.id ?
                // EDIT MODE
                <div className="space-y-4" data-id="element-711">
                              <div className="flex items-center justify-between mb-4" data-id="element-712">
                                <h3 className="text-lg font-bold text-white" data-id="element-713">
                                  Editing Tool
                                </h3>
                                <div className="flex gap-2" data-id="element-714">
                                  <button onClick={() => saveEdit(tool.id)} className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors" title="Save changes" data-id="element-715">
                                    <Save size={18} data-id="element-716" />
                                  </button>
                                  <button onClick={cancelEditing} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors" title="Cancel" data-id="element-717">
                                    <XCircle size={18} data-id="element-718" />
                                  </button>
                                </div>
                              </div>

                              {/* Edit form fields... (keeping existing edit form) */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-719">
                                <div data-id="element-720">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-721">
                                    Tool Name
                                  </label>
                                  <input type="text" value={editForm.name} onChange={e => updateEditForm('name', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-722" />
                                </div>
                                <div data-id="element-723">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-724">
                                    Website URL
                                  </label>
                                  <input type="url" value={editForm.website_url} onChange={e => updateEditForm('website_url', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-725" />
                                </div>
                              </div>

                              <div data-id="element-726">
                                <label className="block text-xs text-gray-400 mb-1" data-id="element-727">
                                  Description
                                </label>
                                <textarea value={editForm.description} onChange={e => updateEditForm('description', e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none resize-none" data-id="element-728" />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-729">
                                <div data-id="element-730">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-731">
                                    Category
                                  </label>
                                  <div className="relative" data-id="element-732">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-400 pointer-events-none" data-id="element-733">
                                      <SelectedCategoryIcon size={16} data-id="element-734" />
                                    </div>
                                    <select value={editForm.category} onChange={e => updateEditForm('category', e.target.value)} className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none appearance-none cursor-pointer" data-id="element-735">
                                      {CATEGORIES.map(cat => <option key={cat.name} value={cat.name} className="bg-gray-900" data-id="element-736">
                                          {cat.name}
                                        </option>)}
                                    </select>
                                  </div>
                                </div>
                                <div data-id="element-737">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-738">
                                    Logo URL
                                  </label>
                                  <input type="url" value={editForm.logo_url} onChange={e => updateEditForm('logo_url', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-739" />
                                </div>
                              </div>

                              <div data-id="element-740">
                                <div className="flex items-center justify-between mb-2" data-id="element-741">
                                  <label className="block text-xs text-gray-400" data-id="element-742">
                                    Pricing Tiers
                                  </label>
                                  <button onClick={addPricingTier} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-id="element-743">
                                    <Plus size={14} data-id="element-744" /> Add Tier
                                  </button>
                                </div>
                                <div className="space-y-2" data-id="element-745">
                                  {editForm.pricing_tiers.map((tier: any, index: number) => <div key={index} className="flex gap-2 items-center" data-id="element-746">
                                        <input type="text" value={tier.name} onChange={e => updatePricingTier(index, 'name', e.target.value)} placeholder="Plan name" className="flex-1 px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-747" />
                                        <input type="text" value={tier.price} onChange={e => updatePricingTier(index, 'price', e.target.value)} placeholder="Price" className="flex-1 px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-748" />
                                        {editForm.pricing_tiers.length > 1 && <button onClick={() => removePricingTier(index)} className="p-2 text-gray-500 hover:text-red-400" data-id="element-749">
                                            <Trash2 size={16} data-id="element-750" />
                                          </button>}
                                      </div>)}
                                </div>
                              </div>
                            </div> :
                // VIEW MODE
                <div className="flex flex-col md:flex-row gap-6" data-id="element-751">
                              <div className="flex-1" data-id="element-752">
                                <div className="flex items-start gap-4 mb-4" data-id="element-753">
                                  {tool.logo_url && <img src={tool.logo_url} alt={tool.name} className="w-12 h-12 rounded-lg object-cover bg-white/10" data-id="element-754" />}
                                  <div data-id="element-755">
                                    <div className="flex items-center gap-2" data-id="element-756">
                                      <h3 className="text-lg font-bold text-white" data-id="element-757">
                                        {tool.name}
                                      </h3>
                                      {tool.is_featured && <span className="px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs font-medium flex items-center gap-1" data-id="element-758">
                                          <Star size={12} fill="currentColor" data-id="element-759" />
                                          Featured
                                        </span>}
                                    </div>
                                    <a href={tool.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm flex items-center gap-1 hover:underline" data-id="element-760">
                                      {tool.website_url}{' '}
                                      <ExternalLink size={12} data-id="element-761" />
                                    </a>
                                  </div>
                                </div>

                                <p className="text-gray-300 mb-4" data-id="element-762">
                                  {tool.description}
                                </p>

                                <div className="flex flex-wrap gap-2" data-id="element-763">
                                  <span className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-764">
                                    {tool.category}
                                  </span>
                                  {tool.pricing_tiers?.map((tier: any, i: number) => <span key={i} className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-765">
                                        {tier.name}: {tier.price}
                                      </span>)}
                                </div>
                              </div>

                              <div className="flex flex-row md:flex-col gap-2 justify-center" data-id="element-766">
                                <button onClick={() => startEditing(tool)} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-cyan-400 transition-colors" title="Edit" data-id="element-767">
                                  <Edit size={18} data-id="element-768" />
                                </button>
                                <button onClick={() => handleToggleFeatured(tool)} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-yellow-400 transition-colors" title={tool.is_featured ? 'Unfeature' : 'Feature'} data-id="element-769">
                                  <Star size={18} fill={tool.is_featured ? 'currentColor' : 'none'} data-id="element-770" />
                                </button>
                                <button onClick={() => handleDeleteTool(tool)} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-red-400 transition-colors" title="Delete" data-id="element-771">
                                  <Trash2 size={18} data-id="element-772" />
                                </button>
                              </div>
                            </div>}
                        </div>)}
                    </div>
                  </div>}

                {/* Scraped Tools Section */}
                {scrapedTools.length > 0 && <div data-id="element-773">
                    <div className="flex items-center gap-3 mb-4" data-id="element-774">
                      <Globe size={20} className="text-cyan-400" data-id="element-775" />
                      <h2 className="text-xl font-bold text-white" data-id="element-776">
                        Scraped Tools ({scrapedTools.length})
                      </h2>
                      <span className="text-sm text-gray-500" data-id="element-777">
                        — Live data from web scraping (read-only)
                      </span>
                    </div>
                    <div className="space-y-4" data-id="element-778">
                      {scrapedTools.map(tool => <div key={tool.id} className="bg-white/5 rounded-xl border border-white/10 p-6 opacity-75" data-id="element-779">
                          <div className="flex flex-col md:flex-row gap-6" data-id="element-780">
                            <div className="flex-1" data-id="element-781">
                              <div className="flex items-start gap-4 mb-4" data-id="element-782">
                                {tool.logo_url && <img src={tool.logo_url} alt={tool.name} className="w-12 h-12 rounded-lg object-cover bg-white/10" data-id="element-783" />}
                                <div data-id="element-784">
                                  <h3 className="text-lg font-bold text-white" data-id="element-785">
                                    {tool.name}
                                  </h3>
                                  <a href={tool.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm flex items-center gap-1 hover:underline" data-id="element-786">
                                    {tool.website_url}{' '}
                                    <ExternalLink size={12} data-id="element-787" />
                                  </a>
                                </div>
                              </div>

                              <p className="text-gray-300 mb-4" data-id="element-788">
                                {tool.description}
                              </p>

                              <div className="flex flex-wrap gap-2" data-id="element-789">
                                <span className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-790">
                                  {tool.category}
                                </span>
                                {tool.pricing_tiers?.map((tier: any, i: number) => <span key={i} className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-791">
                                      {tier.name}: {tier.price}
                                    </span>)}
                              </div>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </>}
          </div>}
      </div>

      <AdminAddToolModal isOpen={isAddToolModalOpen} onClose={() => {
      setIsAddToolModalOpen(false);
      refreshTools();
    }} data-id="element-792" />

      <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={() => setConfirmDialog({
      ...confirmDialog,
      isOpen: false
    })} onConfirm={confirmDialog.onConfirm} title={confirmDialog.title} message={confirmDialog.message} confirmText="Confirm" cancelText="Cancel" variant="danger" data-id="element-793" />
    </div>;
}